import React, { useState, useEffect } from 'react';

// Hooks
import { useDispatch, useSelector } from 'react-redux';

// Actions
import { createContact, updateContact, contactBulkActions } from '~/redux/actions/contacts-actions';
import { fetchSenderProfiles } from '~/redux/actions/sender-profiles-actions';
import { success, failure } from '~/redux/actions/snackbar-actions';

// Utils
import { DEFAULT_PAGINATION, SENDER_PROFILE_LEARN_URL } from '~/utils/constants';
import { debounce } from '~/utils/helperFunctions';
import { MESSAGES } from '~/utils/message';

import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  MenuItem,
  useMediaQuery,
  useTheme,
  Stack,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Input,
} from '@mui/material';
import DataGrid from '~/components/General/OlcDataGrid';
import OlcActionMenu from '~/components/General/OlcActionMenu';
import { MoreVert as MoreVertIcon, Close as CloseIcon } from '@mui/icons-material';

// Styles
import './styles.scss';
import {
  CompanySearchIcon,
  ModalTrashIcon,
  SenderProfileIcon,
  GeneralEditIcon,
  CompanyTrashIcon,
} from '~/assets/images';

// Components
import SenderProfileModal from './SenderProfileModal';
import ConfirmModal from '~/components/General/ConfirmModal';
import NoRecords from '~/components/General/NoRecords';
import TruncatedText from '~/components/General/TruncatedText';
import OlcButton from '~/components/General/OlcButton';
import OlcTablePagination from '~/components/General/OlcTablePagination';

const SenderProfiles = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [activeRow, setActiveRow] = useState(null);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [openConfirmModal, setOpenConfirmModal] = useState({
    open: false,
    message: '',
    header: '',
    icon: null,
    loading: false,
    handleOk: null,
    handleClose: null,
    successButtonName: 'Delete',
    className: 'deleteModal',
  });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchApplied, setSearchApplied] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [showNoRows, setShowNoRows] = useState(false);
  const pageSizes = [20, 50, 100];

  // Redux state
  const senderProfiles = useSelector((state) => state.senderProfilesReducers.senderProfiles);
  const rowsCount = useSelector((state) => state.senderProfilesReducers.rowsCount);
  const loading = useSelector((state) => state.senderProfilesReducers.loading);
  const currentPage = useSelector((state) => state.senderProfilesReducers.currentPage);
  const lastPage = useSelector((state) => state.senderProfilesReducers.lastPage);
  const perPage = useSelector((state) => state.senderProfilesReducers.perPage);
  const from = useSelector((state) => state.senderProfilesReducers.from);
  const to = useSelector((state) => state.senderProfilesReducers.to);

  // Fetch sender profiles function
  const getSenderProfiles = (customPagination = null) => {
    const payload = {
      pagination: customPagination || pagination,
      search: searchTerm.length ? searchTerm : null,
      isReturnAddress: true, // Filter for return addresses only
    };
    dispatch(fetchSenderProfiles(payload));
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value.trimStart());
  };

  // Handle search execution
  const handleSearch = () => {
    if (searchTerm) {
      setPagination({ ...pagination, page: 1 });
      setSearchApplied(true);
      getSenderProfiles();
    }
  };

  // Handle Enter key press in search field
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Debounced functions
  const debouncedHandleKeyDown = debounce(handleSearchKeyPress, 300);
  const debouncedHandleSearch = debounce(handleSearch, 300);

  const handleMenuOpen = (event, profile) => {
    setAnchorEl(event.currentTarget);
    setSelectedProfile(profile);
    setActiveRow(profile);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProfile(null);
    setActiveRow(null);
  };

  const handleProfileAction = (action) => {
    if (action === 'Edit') {
      setModalMode('edit');
      setEditModalOpen(true);
      // Don't clear selectedProfile for edit mode
      setAnchorEl(null);
      setActiveRow(null);
    } else if (action === 'Delete') {
      setOpenConfirmModal({
        open: true,
        message: (
          <>
            Are you sure you want to delete this Sender Profile?
            <br /> It will remain associated with existing orders but won’t be available for new
            orders.
          </>
        ),
        header: 'Delete Sender Profile',
        icon: ModalTrashIcon,
        loading: false,
        handleOk: handleDeleteProfile,
        handleClose: handleDeleteCancel,
        successButtonName: 'Yes',
        className: 'deleteModal',
      });
      // Don't clear selectedProfile for delete mode
      setAnchorEl(null);
      setActiveRow(null);
    } else {
      handleMenuClose();
    }
  };

  const handleDeleteProfile = async () => {
    try {
      setOpenConfirmModal((prev) => ({ ...prev, loading: true }));

      const payload = {
        ids: [selectedProfile.id],
        action: 'delete',
        search: searchTerm.length ? searchTerm : null,
        applyFilters: false,
        filters: null,
      };

      const response = await contactBulkActions(payload);

      if (response.status === 200) {
        dispatch(success(response.data.message || 'Sender profile deleted successfully'));
        setOpenConfirmModal((prev) => ({ ...prev, open: false, loading: false }));
        setSelectedProfile(null);
        getSenderProfiles(); // Refresh the list
      } else {
        dispatch(failure(response?.data?.message || MESSAGES.GENERAL_ERROR));
        setOpenConfirmModal((prev) => ({ ...prev, loading: false }));
      }
    } catch (error) {
      dispatch(failure(error?.message || MESSAGES.GENERAL_ERROR));
      setOpenConfirmModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleDeleteCancel = () => {
    setOpenConfirmModal((prev) => ({ ...prev, open: false }));
    setSelectedProfile(null);
  };

  const handleChangePage = (event, newPage) => {
    setPaginationLoading(true);
    setPagination({ ...pagination, page: newPage });
  };

  const handleChangePageSize = (event) => {
    setPaginationLoading(true);
    const newPagination = { page: 1, pageSize: event.target.value };
    setPagination(newPagination);
    getSenderProfiles(newPagination);
  };

  const open = Boolean(anchorEl);

  // Define loader state
  const showLoader = isInitialLoad || loading || paginationLoading;

  // DataGrid columns configuration
  const columns = [
    {
      field: 'profileName',
      headerName: 'Sender Profile Name',
      flex: 0.8,
      minWidth: isMobile ? 200 : 200,
      compactGrow: 3,
      compactMinWidth: 220,
      compactMaxWidth: 360,
      compactShrinkMinWidth: 110,
      sortable: false,
      renderCell: (params) => <TruncatedText text={params.row.profileName || '-'} maxLength={30} />,
    },
    {
      field: 'fullName',
      headerName: 'Full Name',
      flex: 0.7,
      minWidth: 150,
      compactGrow: 2,
      compactMinWidth: 160,
      compactMaxWidth: 280,
      compactShrinkMinWidth: 88,
      sortable: false,
      renderCell: (params) => <TruncatedText text={params.row.fullName || '-'} maxLength={30} />,
    },
    {
      field: 'companyName',
      headerName: 'Company',
      flex: 0.7,
      minWidth: 150,
      compactGrow: 2,
      compactMinWidth: 170,
      compactMaxWidth: 300,
      compactShrinkMinWidth: 90,
      sortable: false,
      renderCell: (params) => (
        <TruncatedText text={params.row.companyName || '-'} maxLength={30} />
        // <Typography className="fileType">{params.row.companyName || '-'}</Typography>
      ),
    },
    {
      field: 'address1',
      headerName: 'Return Address',
      flex: 1,
      minWidth: 200,
      compactGrow: 3,
      compactMinWidth: 240,
      compactMaxWidth: 480,
      compactShrinkMinWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <TruncatedText
          htmlElement={
            <>
              <Typography
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {(params?.row?.address1 || '') + ' ' + (params?.row?.address2 || '')}
              </Typography>
              <Typography
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {(params?.row?.city || '') +
                  ', ' +
                  (params?.row?.state || '') +
                  ' ' +
                  (params?.row?.zip || '')}
              </Typography>
            </>
          }
          text={
            `${params?.row?.address1} ${params?.row?.address2}` +
            `${params?.row?.city} ` +
            `${params?.row?.state} ` +
            `${params?.row?.zip} `
          }
          maxLength={30}
        />
      ),
    },
    {
      field: 'phoneNo',
      headerName: 'Phone Number',
      flex: 0.7,
      minWidth: 150,
      compactWidth: 170,
      compactShrinkMinWidth: 105,
      sortable: false,
      renderCell: (params) => <TruncatedText text={params.row.phoneNo || '-'} maxLength={15} />,
    },
    {
      field: 'email',
      headerName: 'Email Address',
      flex: 0.8,
      minWidth: 180,
      compactGrow: 3,
      compactMinWidth: 240,
      compactMaxWidth: 420,
      compactShrinkMinWidth: 110,
      sortable: false,
      renderCell: (params) => <TruncatedText text={params.row.email || '-'} maxLength={15} />,
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: (params) => {
        const isMenuOpen = open && activeRow?.id === params.row.id;
        return (
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton
              aria-label={`Open actions for sender ${params.row.name || params.row.email}`}
              id={`sender-profile-actions-${params.row.id}`}
              aria-controls={isMenuOpen ? 'sender-profiles-actions-menu' : undefined}
              aria-expanded={isMenuOpen ? 'true' : undefined}
              aria-haspopup="true"
              onClick={(event) => handleMenuOpen(event, params.row)}
              className="olcTableDropdownIcon"
              sx={{
                opacity: isMenuOpen ? '0.8 !important' : '0.4 !important',
              }}
            >
              <MoreVertIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  // Handle modal open/close
  const handleOpenModal = () => {
    setModalMode('add');
    setSelectedProfile(null);
    setAddModalOpen(true);
  };

  const handleCloseModal = () => {
    setAddModalOpen(false);
    setEditModalOpen(false);
    setSelectedProfile(null);
  };

  // useEffect to fetch data on component mount
  useEffect(() => {
    getSenderProfiles();
  }, []);

  // useEffect to handle initial load completion
  useEffect(() => {
    if (!loading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [loading, isInitialLoad]);

  // useEffect to handle pagination changes
  useEffect(() => {
    if (pagination.page !== currentPage) {
      getSenderProfiles();
    }
  }, [pagination.page, pagination.pageSize]);

  // useEffect to handle search clearing
  useEffect(() => {
    if (!searchTerm && !isInitialLoad) {
      setSearchApplied(false);
      setSearchTerm('');
      getSenderProfiles();
    }
  }, [searchTerm]);

  // Reset pagination loading when new data arrives
  useEffect(() => {
    setPaginationLoading(false);
  }, [senderProfiles]);

  // Add delay for showing no rows overlay
  useEffect(() => {
    let timeoutId;

    if (!(isInitialLoad || loading || paginationLoading) && senderProfiles.length === 0) {
      timeoutId = setTimeout(() => {
        setShowNoRows(true);
      }, 300);
    } else {
      setShowNoRows(false);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isInitialLoad, loading, paginationLoading, senderProfiles.length]);

  return (
    <Box
      className="companyProfileTableTabContent"
      sx={{ px: senderProfiles.length === 0 && !loading ? '' : '16px' }}
    >
      {/* Files List */}
      <div className="companyProfileTableBody">
        <Box
          className={`brandingAssetsHeader ${senderProfiles.length === 0 && !searchApplied ? 'isEmpty' : ''}`}
        >
          {(senderProfiles.length > 0 || searchApplied) && (
            <Box className="searchWrapper olcSearchWrapper">
              <Input
                className={`searchInput ${searchApplied ? 'searchApplied' : ''}`}
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={debouncedHandleKeyDown}
                placeholder="Search all fields"
                title="Search all fields"
                endAdornment={
                  <InputAdornment position="end" className="searchInputAdornment">
                    <IconButton
                      aria-label="search"
                      edge="end"
                      className="searchIcon"
                      onClick={debouncedHandleSearch}
                    >
                      <img src={CompanySearchIcon} alt="search" />
                    </IconButton>
                    {searchTerm?.trim() && (
                      <IconButton
                        className="cpClearInputBtn"
                        size="small"
                        onClick={() => {
                          setSearchTerm('');
                          setSearchApplied(false);
                        }}
                        edge="end"
                        sx={{ padding: '4px' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </InputAdornment>
                }
              />
            </Box>
          )}

          <div className="cpAddNewButton">
            <OlcButton variant="primary" onClick={handleOpenModal}>
              Add New
            </OlcButton>
          </div>
        </Box>

        {senderProfiles.length === 0 && !loading && !searchApplied && !isInitialLoad ? (
          <Stack
            className="noRecordsWrapperHere"
            style={{ marginTop: '20px', padding: '0px 16px 40px 16px' }}
          >
            <NoRecords
              linkText={'Learn more'}
              linkUrl={SENDER_PROFILE_LEARN_URL}
              icon={
                <Box style={{ width: '40px', height: '40px', margin: '0px' }}>
                  <img
                    src={SenderProfileIcon}
                    style={{ width: '37px', height: '37px' }}
                    alt="sender profile"
                  />
                </Box>
              }
              title="No Sender Profiles Yet"
              message="Store the name, company, return address, and contact details that appear on your mail pieces. Create
multiple profiles to send on behalf of different people or brands while staying organized and consistent."
              messageWidth="80%"
            />
          </Stack>
        ) : (
          <>
            <Box
              className={`olcTableGridInternal ${showLoader ? 'table-loading' : ''} ${senderProfiles.length === 0 ? 'table-empty' : ''}`}
              sx={{
                // Add horizontal scroll for screens less than 1500px
                '@media (max-width: 1500px)': {
                  overflowX: 'auto',
                  width: '100%',
                  '&::-webkit-scrollbar': {
                    height: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'var(--olc-scrollbar-track)',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'var(--olc-scrollbar-thumb)',
                    borderRadius: '4px',
                    '&:hover': {
                      background: 'var(--olc-scrollbar-thumb-hover)',
                    },
                  },
                },
              }}
            >
              <Box sx={{ position: 'relative' }}>
                <DataGrid
                  key={JSON.stringify(
                    senderProfiles.map((p) => ({
                      id: p.id,
                      profileName: p.profileName,
                      websiteUrl: p.websiteUrl,
                      fullName: p.fullName,
                      companyName: p.companyName,
                      address1: p.address1,
                      phoneNo: p.phoneNo,
                      email: p.email,
                    }))
                  )}
                  rows={senderProfiles}
                  columns={columns}
                  getRowId={(row) => row.id}
                  disableColumnMenu
                  disableRowSelectionOnClick
                  pagination={false}
                  rowHeight={61}
                  loading={showLoader}
                  shrinkColumnsToFit
                  sx={{
                    width: '100%',
                    '& .MuiDataGrid-cell': {
                      overflow: 'hidden !important',
                    },
                    '& .MuiDataGrid-columnHeader': {
                      overflow: 'hidden !important',
                    },
                    ...(showNoRows ||
                    (!(isInitialLoad || loading || paginationLoading) &&
                      senderProfiles.length === 0)
                      ? {
                          height: 'auto',
                          minHeight: '61px',
                        }
                      : {}),
                  }}
                  slots={{
                    noRowsOverlay: () =>
                      showNoRows ? (
                        <Typography className="noResultsText">No Results Found</Typography>
                      ) : null,
                    noResultsOverlay: () =>
                      showNoRows ? (
                        <Typography className="noResultsText">No Results Found</Typography>
                      ) : null,
                  }}
                />
              </Box>
            </Box>
          </>
        )}
      </div>

      {/* Pagination */}
      {(showLoader || rowsCount > 0) && (
        <OlcTablePagination
          count={lastPage}
          page={currentPage}
          pageSize={perPage || pagination.pageSize}
          pageSizes={pageSizes}
          onPageChange={handleChangePage}
          onPageSizeChange={handleChangePageSize}
          from={from}
          to={to}
          total={rowsCount}
          loading={showLoader}
        />
      )}

      {/* Menu Actions */}
      <OlcActionMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => handleProfileAction('Edit')}>
          <img src={GeneralEditIcon} alt="edit" />
          Edit
        </MenuItem>
        <MenuItem onClick={() => handleProfileAction('Delete')}>
          <img src={CompanyTrashIcon} alt="delete" />
          Delete
        </MenuItem>
      </OlcActionMenu>

      {/* Sender Profile Modal */}
      <SenderProfileModal
        openModal={addModalOpen || editModalOpen}
        handleCloseModal={handleCloseModal}
        selectedProfile={selectedProfile}
        mode={modalMode}
        onSuccess={() => getSenderProfiles()}
      />

      {/* Confirm Modal */}
      <ConfirmModal openConfirmModal={openConfirmModal} />
    </Box>
  );
};

export default SenderProfiles;
